import { useState } from 'react';

import { useTheme } from '@mui/material';
import { useForm } from 'react-final-form';

import { HibernationSchedule } from '@cast/types-cluster-autoscaler';

import { SavedSchedulesPicker } from './SavedSchedulesPicker';

import { RffJsonEditor } from '@cast/shared-rff-ui';

import { Typography, Stack } from '@cast/design-system';

type NodeConfigurationProps = {
  isEditMode: boolean;
  hibernationSchedules?: HibernationSchedule[];
  onScheduleSelect: (schedule: HibernationSchedule) => void;
};

export const NodeConfiguration = ({
  isEditMode,
  hibernationSchedules,
  onScheduleSelect,
}: NodeConfigurationProps) => {
  const { palette } = useTheme();
  const form = useForm();
  const [selectedSchedule, setSelectedSchedule] =
    useState<HibernationSchedule | null>(null);

  const handleScheduleSelect = (schedule: HibernationSchedule) => {
    setSelectedSchedule(schedule);

    const newNodeJobConfigValue = JSON.stringify(
      schedule.resumeConfig.jobConfig
    );

    form.change('nodeJobConfig', newNodeJobConfigValue);
    // Reset the form state in order to detect further changes to the JSON
    form.reset({
      nodeJobConfig: newNodeJobConfigValue,
    });

    onScheduleSelect(schedule);
  };

  return (
    <Stack gap={8}>
      {isEditMode && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="P12M">Node configuration JSON</Typography>
          <SavedSchedulesPicker
            schedules={hibernationSchedules}
            selectedSchedule={selectedSchedule}
            onSelect={handleScheduleSelect}
          />
        </Stack>
      )}
      <RffJsonEditor
        testId="node-job-config-editor"
        name="nodeJobConfig"
        placeholder="{}"
        sx={{
          overflow: 'auto',
          maxHeight: '378px',
          '& .CodeEditor-editor': {
            minHeight: '300px',
            backgroundColor: isEditMode ? palette.grey[900] : palette.grey[700],
          },
        }}
      />
      {!!selectedSchedule && (
        <Typography
          variant="P12R"
          color="textSecondary"
          data-testid="node-config-modification-message"
        >
          {form.getFieldState('nodeJobConfig')?.dirty
            ? 'Edited based on'
            : 'Imported from'}
          : {selectedSchedule.name}
        </Typography>
      )}
    </Stack>
  );
};
